/**
 * "Depreciation" tab for an inventory record (equipment / component). Top: summary
 * cards read straight off the item (purchase value, current value, rate, computed
 * depreciated amount). Write actions: Calculate (POST /items/:id/depreciate for N
 * months) and Update Settings (PUT /items/:id/depreciation for rate + current_value
 * override). Below: a history DataTable fed by `useDepreciationHistory`. The history
 * endpoint may 500 on staging; on error / empty we degrade to an EmptyState instead
 * of crashing the tab.
 */
import { useState, type FormEvent } from 'react';
import { TrendingDown, Calculator, Settings } from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle, CardFooter, Chip, Input, Button } from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, Stat, StatGroup, type ColumnDef } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { Field } from '@vnetwork/ui/forms';
import type {
  DepreciationEntry,
  Item,
  CalculateDepreciationBody,
  UpdateDepreciationBody,
} from '@/api/capacities';
import {
  useCalculateDepreciation,
  useUpdateDepreciation,
} from '@/api/capacities';
import { formatDate, formatMoney } from '@/lib/format';
import { TabLoading } from '@/pages/customers/detail/tab-states';
import { NumericInput } from '@/components/numeric-input';

const muted = { color: 'var(--vn-fg-muted)' } as const;
const dash = <span style={muted}>—</span>;

/** Computed drop from purchase price to current value (never negative). */
function depreciatedAmount(item: Item): number | undefined {
  const { purchase_price: p, current_value: c } = item;
  if (p == null || c == null) return undefined;
  return Math.max(0, p - c);
}

/** previous → new value cell, green when value rose, red when it dropped. */
function valueChangeCell(entry: DepreciationEntry) {
  const { previous_value: prev, new_value: next } = entry;
  if (prev == null && next == null) return dash;
  const color =
    prev != null && next != null && next !== prev
      ? next > prev
        ? 'var(--vn-success-text)'
        : 'var(--vn-danger-text)'
      : 'var(--vn-fg-default)';
  return (
    <span style={{ display: 'inline-flex', gap: 'var(--vn-space-1)', alignItems: 'center' }}>
      <span style={muted}>{prev != null ? formatMoney(prev) : '—'}</span>
      <span style={muted}>→</span>
      <span style={{ color, fontWeight: 'var(--vn-font-weight-medium)' }}>
        {next != null ? formatMoney(next) : '—'}
      </span>
    </span>
  );
}

const columns: ColumnDef<DepreciationEntry, unknown>[] = [
  {
    id: 'action',
    header: 'Action',
    cell: ({ row }) =>
      row.original.action ? (
        <Chip tone="info">{row.original.action.replace(/_/g, ' ')}</Chip>
      ) : (
        dash
      ),
  },
  { id: 'value', header: 'Value', cell: ({ row }) => valueChangeCell(row.original) },
  {
    id: 'rate',
    header: 'Rate',
    cell: ({ row }) => (row.original.depreciation_rate != null ? `${row.original.depreciation_rate}%` : dash),
  },
  {
    id: 'months',
    header: 'Months',
    cell: ({ row }) => (row.original.months != null ? String(row.original.months) : dash),
  },
  {
    id: 'reason',
    header: 'Reason',
    cell: ({ row }) => row.original.reason || dash,
  },
  {
    id: 'date',
    header: 'Date',
    cell: ({ row }) => (row.original.created_at ? formatDate(row.original.created_at) : dash),
  },
];

/** Inline form for Calculate Depreciation (straight-line N months). */
function CalculateForm({ itemId }: { itemId: string }) {
  const [months, setMonths] = useState('');
  const [reason, setReason] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const calc = useCalculateDepreciation();

  const monthsNum = parseInt(months, 10);
  const monthsInvalid = !months || isNaN(monthsNum) || monthsNum < 1;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (monthsInvalid) return;
    const body: CalculateDepreciationBody = { months: monthsNum };
    if (reason.trim()) body.reason = reason.trim();
    calc.mutate({ id: itemId, body }, { onSuccess: () => { setMonths(''); setReason(''); setSubmitted(false); } });
  };

  return (
    <Card flat>
      <CardHeader>
        <CardTitle>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
            <Calculator size={15} />
            Calculate Depreciation
          </span>
        </CardTitle>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          <Alert tone="warning" title="Live action">
            This writes a depreciation entry to the live warehouse service immediately.
          </Alert>
          {calc.isError ? (
            <Alert tone="danger" title="Failed to calculate depreciation">
              Please try again.
            </Alert>
          ) : null}
          {calc.isSuccess ? (
            <Alert tone="success" title="Depreciation calculated successfully" />
          ) : null}
          <Field
            label="Months"
            required
            error={(submitted && monthsInvalid) ? 'Enter a positive whole number of months' : undefined}
          >
            {(p) => (
              <NumericInput
                {...p}
                min="1"
                step="1"
                value={months}
                invalid={submitted && monthsInvalid}
                onChange={(e) => setMonths(e.target.value)}
                placeholder="e.g. 12"
                style={{ maxWidth: 160 }}
              />
            )}
          </Field>
          <Field label="Reason">
            {(p) => (
              <Input
                {...p}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Optional reason"
              />
            )}
          </Field>
        </CardBody>
        <CardFooter style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="submit" variant="primary" loading={calc.isPending} disabled={calc.isPending}>
            Calculate
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}

/** Inline form for Update Depreciation Settings (rate + current_value override). */
function UpdateSettingsForm({ itemId, item }: { itemId: string; item: Item }) {
  const [rate, setRate] = useState(item.depreciation_rate != null ? String(item.depreciation_rate) : '');
  const [currentValue, setCurrentValue] = useState(item.current_value != null ? String(item.current_value) : '');
  const [reason, setReason] = useState('');
  const update = useUpdateDepreciation();

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const body: UpdateDepreciationBody = {};
    const rateNum = parseFloat(rate);
    const valueNum = parseFloat(currentValue);
    if (!isNaN(rateNum)) body.depreciation_rate = rateNum;
    if (!isNaN(valueNum)) body.current_value = valueNum;
    if (reason.trim()) body.reason = reason.trim();
    update.mutate({ id: itemId, body }, { onSuccess: () => setReason('') });
  };

  return (
    <Card flat>
      <CardHeader>
        <CardTitle>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
            <Settings size={15} />
            Update Depreciation Settings
          </span>
        </CardTitle>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          <Alert tone="warning" title="Live action">
            This updates the item's depreciation settings in the live warehouse service immediately.
          </Alert>
          {update.isError ? (
            <Alert tone="danger" title="Failed to update depreciation settings">
              Please try again.
            </Alert>
          ) : null}
          {update.isSuccess ? (
            <Alert tone="success" title="Depreciation settings updated" />
          ) : null}
          <Field label="Depreciation Rate (%)">
            {(p) => (
              <NumericInput
                {...p}
                min="0"
                max="100"
                step="0.01"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                placeholder="e.g. 10"
                style={{ maxWidth: 160 }}
              />
            )}
          </Field>
          <Field label="Current Value">
            {(p) => (
              <NumericInput
                {...p}
                min="0"
                step="0.01"
                value={currentValue}
                onChange={(e) => setCurrentValue(e.target.value)}
                placeholder="e.g. 5000000"
                style={{ maxWidth: 200 }}
              />
            )}
          </Field>
          <Field label="Reason">
            {(p) => (
              <Input
                {...p}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Optional reason for this update"
              />
            )}
          </Field>
        </CardBody>
        <CardFooter style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="submit" variant="primary" loading={update.isPending} disabled={update.isPending}>
            Save Settings
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}

export interface DepreciationTabProps {
  /** Item id (used for mutation endpoints). */
  itemId: string;
  item: Item;
  history: DepreciationEntry[];
  isLoading: boolean;
  isError: boolean;
}

/** Summary cards from the item + depreciation history table (graceful on failure) + write actions. */
export function DepreciationTab({ itemId, item, history, isLoading, isError }: DepreciationTabProps) {
  const depreciated = depreciatedAmount(item);

  const emptyHistory = (
    <EmptyState
      icon={<TrendingDown size={24} />}
      title="No depreciation history"
      description="No depreciation has been recorded for this item yet."
    />
  );

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Card flat>
        <CardBody>
          <StatGroup columns={4}>
            <Stat
              label="Purchase Value"
              value={item.purchase_price != null ? formatMoney(item.purchase_price) : '—'}
            />
            <Stat
              label="Current Value"
              value={item.current_value != null ? formatMoney(item.current_value) : '—'}
            />
            <Stat
              label="Depreciation Rate"
              value={item.depreciation_rate != null ? `${item.depreciation_rate}%` : '—'}
            />
            <Stat
              label="Depreciated Amount"
              value={depreciated != null ? formatMoney(depreciated) : '—'}
            />
          </StatGroup>
        </CardBody>
      </Card>

      <div
        style={{
          display: 'grid',
          gap: 'var(--vn-space-4)',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        }}
      >
        <CalculateForm itemId={itemId} />
        <UpdateSettingsForm itemId={itemId} item={item} />
      </div>

      {isLoading ? (
        <TabLoading rows={4} />
      ) : isError || history.length === 0 ? (
        <Card flat>
          <CardBody>{emptyHistory}</CardBody>
        </Card>
      ) : (
        <DataTable
          columns={columns}
          data={history}
          getRowId={(r, i) => r.id ?? String(i)}
          emptyState={emptyHistory}
        />
      )}
    </div>
  );
}
